/** A checkable word and where it starts in the text it came from. */
export interface WordToken {
  word: string
  start: number
  /**
   * The whole hyphenated word this piece belongs to ("zzqqx-yywwv" for "zzqqx"), set only when the
   * word was split. A dictionary entry may be the compound itself, so it is looked up whole before
   * its pieces are judged.
   */
  compound?: string
}

const WORD_RUN = /[\p{L}\p{M}\p{N}'’-]+/gu
const EDGE_PUNCTUATION = /^['’-]+|['’-]+$/g
const LEADING_PUNCTUATION = /^['’-]+/

/** Web addresses and email addresses: not words, so never checked. */
const ADDRESS =
  /(?:https?:\/\/|www\.)\S+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+|\b[\w-]+(?:\.[\w-]+)*\.(?:com|net|org|edu|gov|io|co|us)\b\S*/giu

function addressRanges(text: string): Array<[number, number]> {
  return [...text.matchAll(ADDRESS)].map((match) => {
    const start = match.index ?? 0

    return [start, start + match[0].length]
  })
}

const cleanWord = (word: string) => word.replace(EDGE_PUNCTUATION, '').replace(/’/g, "'")

/**
 * Splits text into the words worth checking. Leading and trailing apostrophes and hyphens are
 * stripped, hyphenated compounds are checked piece by piece ("hard-fought" → "hard", "fought"),
 * and anything containing a digit or a lone letter is skipped, as are web and email addresses.
 */
export function tokenizeWords(text: string): WordToken[] {
  const tokens: WordToken[] = []
  const skipped = addressRanges(text)

  for (const match of text.matchAll(WORD_RUN)) {
    let offset = match.index ?? 0

    if (skipped.some(([from, to]) => offset >= from && offset < to)) continue

    const pieces = match[0].split('-')
    const compound = pieces.length > 1 ? cleanWord(match[0]) : undefined

    for (const piece of pieces) {
      const word = cleanWord(piece)

      if (word.length > 1 && !/\p{N}/u.test(word)) {
        const leading = piece.length - piece.replace(LEADING_PUNCTUATION, '').length

        tokens.push({
          word,
          start: offset + leading,
          ...(compound?.includes('-') && { compound }),
        })
      }

      offset += piece.length + 1
    }
  }

  return tokens
}
